import React, { ReactNode, useContext } from 'react';
import classNames from 'classnames';
import { GatedProductContext } from 'src/shared/components/product-gate/GatedProductContext';
import ProductVisibilityNotice from 'src/shared/components/product-visibility-notice/ProductVisibilityNotice';
import styles from './StandardAppLayout.module.scss';
import Breadcrumbs, { Breadcrumb } from '../../breadcrumbs/Breadcrumbs';

export type StandardAppLayoutProps = {
    title?: string;
    subTitle?: ReactNode;
    breadcrumbs?: Breadcrumb[];
    otherActions?: ReactNode;
    mainContent?: ReactNode;
    lozenges?: ReactNode;
    filterSection?: ReactNode;
    classNames?: {
        mainContent?: string;
        otherActions?: string;
        topContent?: string;
    };
    topBanner?: ReactNode;
    /** When false, the whole layout (including header) scrolls as one unit. Defaults to true (sticky header). */
    stickyHeader?: boolean;
};

export const StandardAppLayout = (props: StandardAppLayoutProps) => {
    const {
        title,
        subTitle,
        breadcrumbs,
        otherActions,
        mainContent,
        lozenges,
        filterSection,
        classNames: classNamesFromProps,
        topBanner,
        stickyHeader = true,
    } = props;
    // Every page behind a ProductGate tells internal staff what the customer does not see.
    const gatedProduct = useContext(GatedProductContext);
    const shouldShowTopContent = title || subTitle || lozenges || otherActions || breadcrumbs;

    const mainContentClassNames = classNames(styles.mainContent, {
        [styles.mainContentScrollable]: stickyHeader,
        [classNamesFromProps?.mainContent || '']: classNamesFromProps?.mainContent,
    });

    const otherActionsClassNames = classNames(styles.filters, {
        [classNamesFromProps?.otherActions || '']: classNamesFromProps?.otherActions,
    });

    const topContentClassNames = classNames(styles.topContent, {
        [classNamesFromProps?.topContent || '']: classNamesFromProps?.topContent,
    });

    return (
        <div
            className={classNames(styles.standardAppLayout, { [styles.standardAppLayoutScrollable]: !stickyHeader })}
            data-testid='standard-app-layout'
        >
            {gatedProduct && <ProductVisibilityNotice product={gatedProduct} />}
            {topBanner}
            {shouldShowTopContent && (
                <div className={topContentClassNames}>
                    <div className={classNames(styles.titleBar, 'flex flex-col gap-2')}>
                        {breadcrumbs && (
                            <div className={styles.breadcrumbs}>
                                <Breadcrumbs breadcrumbs={breadcrumbs} />
                            </div>
                        )}
                        {title && (
                            <h1 className='text-3xl leading-8 font-normal m-0 text-foreground' data-testid='title'>
                                {title}
                            </h1>
                        )}
                        {subTitle && (
                            <h2 className='text-xl leading-6 font-normal m-0 text-muted-foreground' data-testid='sub-title'>
                                {subTitle}
                            </h2>
                        )}
                    </div>
                    <div className={otherActionsClassNames}>
                        <div className={styles.habitatAssay}>{lozenges || null}</div>
                        {otherActions && <div className={styles.otherActions}>{otherActions}</div>}
                    </div>
                </div>
            )}
            <div className={styles.filters} data-testid='filter-section'>
                {filterSection || null}
            </div>

            <div className={mainContentClassNames} data-testid='main-content'>
                {mainContent || null}
            </div>
        </div>
    );
};

export default StandardAppLayout;
